/**
 * Bulk Upload Service for Guest Blog Sites Frontend
 */

import { apiService } from './api';

export interface ColumnMapping {
  csvColumn: string;
  guestBlogSiteField: string;
}

export interface ValidationError {
  row: number;
  field: string;
  value: any;
  error: string;
}

export interface PreviewRow {
  rowIndex: number;
  site_url: string;
  da: number;
  dr: number;
  ahrefs_traffic: number;
  ss?: number;
  tat: string;
  category: string;
  status: string;
  negotiation_status: string;
  base_price: number;
  li_base_price?: number;
  country: string;
  publisher: string;
  site_language: string;
  outreach_email?: string;
  displayed_price: number;
  li_displayed_price?: number;
  isValid: boolean;
  errors: ValidationError[];
}

export interface BulkUploadPreview {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  previewData: PreviewRow[];
  columnMappings: ColumnMapping[];
  availableColumns: string[];
}

export interface BulkUploadResult {
  saved: number;
  errors: string[];
  message: string;
  details?: {
    created: number;
    updated: number;
    skipped: number;
    totalProcessed: number;
    actionDetails: Array<{ action: string; site_url: string; message: string }>;
  };
  summary?: {
    totalProcessed: number;
    successful: number;
    created: number;
    updated: number;
    skipped: number;
    errors: number;
    message: string;
  };
}

export interface ParseResponse {
  totalRows: number;
  availableColumns: string[];
  autoMappings: ColumnMapping[];
  guestBlogSiteColumns: Array<{
    key: string;
    label: string;
    required: boolean;
  }>;
  clientPercentage: number;
  sessionId: string;
}

class BulkUploadService {
  /**
   * Download CSV template
   */
  async downloadTemplate(): Promise<Blob> {
    // For blob downloads, we need to use fetch directly with proper base URL
    const token = localStorage.getItem('token');
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const response = await fetch(`${baseURL}/api/guest-sites/bulk-upload/template`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to download template: ${response.statusText}`);
    }
    
    return response.blob();
  }

  /**
   * Parse uploaded file and get initial data
   */
  async parseFile(file: File, clientId?: string): Promise<ParseResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (clientId) {
      formData.append('clientId', clientId);
    }

    const response = await apiService.upload<{ success: boolean; data: ParseResponse }>('/guest-sites/bulk-upload/parse', formData);
    return response.data;
  }

  /**
   * Generate preview with column mappings
   */
  async generatePreview(
    file: File,
    mappings: ColumnMapping[],
    clientId?: string
  ): Promise<BulkUploadPreview> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mappings', JSON.stringify(mappings));
    if (clientId) {
      formData.append('clientId', clientId);
    }

    const response = await apiService.upload<{ success: boolean; data: BulkUploadPreview }>('/guest-sites/bulk-upload/preview', formData);
    return response.data;
  }

  /**
   * Save selected rows from preview.
   *
   * Single-shot saves of 500+ rows hit the reverse proxy's upstream-idle
   * timeout (~60s) and surface as a 502 Bad Gateway in the browser. We work
   * around that by sending batches of `chunkSize` rows: each batch finishes
   * in seconds, well under any proxy timeout, and we merge the results.
   *
   * - Sends a single request when totalRows <= chunkSize (no behavior change).
   * - Otherwise splits selectedRows into batches and sends them sequentially
   *   (so the backend doesn't get hit by parallel duplicate-detection races).
   * - Each batch payload only includes the preview rows referenced by that
   *   batch's selectedRows, so the JSON stays small.
   * - `onProgress` fires after each batch with rows processed and percent.
   */
  async savePreviewData(
    previewData: PreviewRow[],
    selectedRows: number[],
    options?: {
      chunkSize?: number;
      onProgress?: (info: { processed: number; total: number; percent: number; batchIndex: number; totalBatches: number }) => void;
    }
  ): Promise<BulkUploadResult> {
    const chunkSize = options?.chunkSize ?? 100;
    const onProgress = options?.onProgress;
    const total = selectedRows.length;

    // Small saves go through unchanged.
    if (total <= chunkSize) {
      const response = await apiService.post<{ success: boolean; data: BulkUploadResult }>(
        '/guest-sites/bulk-upload/save',
        { previewData, selectedRows },
        { timeout: 300000 }
      );
      onProgress?.({
        processed: total,
        total,
        percent: 100,
        batchIndex: 1,
        totalBatches: 1,
      });
      return response.data;
    }

    const totalBatches = Math.ceil(total / chunkSize);

    const merged: BulkUploadResult = {
      saved: 0,
      errors: [],
      message: '',
      details: {
        created: 0,
        updated: 0,
        skipped: 0,
        totalProcessed: 0,
        actionDetails: [],
      },
    };

    let processed = 0;

    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      const start = batchIndex * chunkSize;
      const batchSelectedRows = selectedRows.slice(start, start + chunkSize);

      // Only ship the preview rows referenced by this batch — keeps the
      // JSON payload small and avoids re-uploading the entire dataset
      // for every batch.
      const wantedSet = new Set(batchSelectedRows);
      const batchPreview = previewData.filter((row) => wantedSet.has(row.rowIndex));

      const response = await apiService.post<{ success: boolean; data: BulkUploadResult }>(
        '/guest-sites/bulk-upload/save',
        { previewData: batchPreview, selectedRows: batchSelectedRows },
        { timeout: 300000 }
      );
      const batchResult = response.data;

      merged.saved += batchResult.saved || 0;
      if (batchResult.errors?.length) {
        merged.errors.push(...batchResult.errors);
      }
      if (batchResult.details && merged.details) {
        merged.details.created += batchResult.details.created || 0;
        merged.details.updated += batchResult.details.updated || 0;
        merged.details.skipped += batchResult.details.skipped || 0;
        merged.details.totalProcessed += batchResult.details.totalProcessed || 0;
        if (batchResult.details.actionDetails?.length) {
          merged.details.actionDetails.push(...batchResult.details.actionDetails);
        }
      }

      processed += batchSelectedRows.length;
      onProgress?.({
        processed,
        total,
        percent: Math.round((processed / total) * 100),
        batchIndex: batchIndex + 1,
        totalBatches,
      });
    }

    merged.message = `Bulk upload completed: ${merged.saved} of ${total} rows saved.`;
    merged.summary = {
      totalProcessed: total,
      successful: merged.saved,
      created: merged.details?.created || 0,
      updated: merged.details?.updated || 0,
      skipped: merged.details?.skipped || 0,
      errors: merged.errors.length,
      message: merged.message,
    };

    return merged;
  }
}

export const bulkUploadService = new BulkUploadService();
